import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { CreateNoteRequest, Note, NotesService } from '../notes/notes.service';

@Component({
  selector: 'hb-notes-dialog',
  templateUrl: './notes-dialog.component.html',
  styleUrls: ['./notes-dialog.component.scss']
})
export class NotesDialogComponent implements OnInit {
  @Input() inspectionPlanTaskId: string;
  @Input() taskReference: string;
  @Input() methodName: string;
  @ViewChild('noteContentInput') noteContentInput: ElementRef;

  public notes: Note[] = [];
  public isEditMode: boolean = false;
  public editingNote: Note | null = null;
  public newNoteContent: string = '';
  public loading: boolean = false;
  public showDeleteConfirmation: boolean = false;
  public noteToDelete: Note | null = null;

  public onClose: Subject<boolean> = new Subject();

  constructor(
    private bsModalRef: BsModalRef,
    private notesService: NotesService,
    private authService: AuthService,
    private modalService: BsModalService
  ) { }

  ngOnInit(): void {
    this.loadNotes();
  }

  private loadNotes(): void {
    if (!this.inspectionPlanTaskId) return;

    this.loading = true;
    this.notesService.getNotesByTaskId(this.inspectionPlanTaskId).subscribe({
      next: (notes) => {
        this.notes = notes;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading notes:', error);
        this.loading = false;
      }
    });
  }

  public addNote(): void {
    this.isEditMode = true;
    this.editingNote = null;
    this.focusNoteContent();
    this.newNoteContent = '';
  }

  public editNote(note: Note): void {
    // Check if the current user is the creator of the note
    if (!this.canEditNote(note)) {
      alert('You can only edit notes that you created.');

      return;
    }

    this.isEditMode = true;
    this.editingNote = note;
    this.focusNoteContent();
    this.newNoteContent = note.noteContent;
  }

  public saveNote(): void {
    if (!this.newNoteContent.trim()) {
      return;
    }

    this.loading = true;

    if (this.editingNote) {
      // Update existing note - send complete note with updated content
      const updateRequest: Note = {
        ...this.editingNote,
        noteContent: this.newNoteContent
      };

      this.notesService.updateNote(this.editingNote.noteId, updateRequest).subscribe({
        next: (updatedNote) => {
          const index = this.notes.findIndex(n => n.noteId === updatedNote.noteId);
          if (index !== -1) {
            this.notes[index] = updatedNote;
          }
          this.cancelEdit();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error updating note:', error);
          this.loading = false;
        }
      });
    } else {
      // Create new note
      const createRequest: CreateNoteRequest = {
        identifierId: this.inspectionPlanTaskId,
        identifierType: 'InspectionTask',
        noteContent: this.newNoteContent
      };

      this.notesService.createNote(createRequest).subscribe({
        next: (newNote) => {
          this.notes.push(newNote);
          this.cancelEdit();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error creating note:', error);
          this.loading = false;
        }
      });
    }
  }

  public deleteNote(note: Note): void {
    // Check if the current user is the creator of the note
    if (!this.canDeleteNote(note)) {
      alert('You can only delete notes that you created.');

      return;
    }

    // Show confirmation dialog instead of native confirm
    this.noteToDelete = note;
    this.showDeleteConfirmation = true;
  }

  public confirmDelete(): void {
    if (!this.noteToDelete) {
      return;
    }

    this.loading = true;
    this.notesService.deleteNote(this.noteToDelete.noteId).subscribe({
      next: () => {
        this.notes = this.notes.filter(n => n.noteId !== this.noteToDelete!.noteId);
        this.loading = false;
        this.cancelDelete();
      },
      error: (error) => {
        console.error('Error deleting note:', error);
        this.loading = false;
        this.cancelDelete();
      },
    });
  }

  public cancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.noteToDelete = null;
  }

  public canDeleteNote(note: Note): boolean {
    const currentUserId = this.authService.userId;

    return currentUserId === note.createdBy;
  }

  public canEditNote(note: Note): boolean {
    const currentUserId = this.authService.userId;

    return currentUserId === note.createdBy;
  }

  public cancelEdit(): void {
    this.isEditMode = false;
    this.editingNote = null;
    this.newNoteContent = '';
  }

  public close(): void {
    this.onClose.next(true);
    this.bsModalRef.hide();
  }

  public formatDate(date: Date): string {
    return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
  }

  private focusNoteContent(): void {

    setTimeout(() => {
      if (this.noteContentInput && this.noteContentInput.nativeElement) {
        this.noteContentInput.nativeElement.focus();
      }
    }, 0);
  }
}
